'use client';

import { useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  AUTH_REDIRECT_KEY,
  buildAccountsSignInUrl,
  safeRedirectTarget,
} from '@/lib/auth/client-constants';
import { ensureValidToken } from '@/lib/auth/client-refresh';
import { decodeJwtPayload, hashToken } from '@/lib/auth/decode-jwt';
import { installAuthAxiosInterceptor } from '@/lib/auth/install-axios-interceptor';
import type { AuthUser } from '@/lib/auth/types';
import { scheduleParamRemoval } from '@/lib/url-cleaner';
import {
  clearAuthCache,
  readAuthCache,
  useNewAuthStore,
  writeAuthCache,
} from '@/store/use-new-auth-store';

const COOKIE_TOKEN = 'token';

interface NewAuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (redirectTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const NewAuthContext = createContext<NewAuthContextValue | null>(null);

export function NewAuthProvider({ children }: { children: ReactNode }) {
  const user = useNewAuthStore.use.user();
  const isAuthenticated = useNewAuthStore.use.isAuthenticated();
  const isLoading = useNewAuthStore.use.isLoading();

  const setUser = useNewAuthStore.use.setUser();
  const setCustomerId = useNewAuthStore.use.setCustomerId();
  const setIsLoading = useNewAuthStore.use.setIsLoading();
  const resetStore = useNewAuthStore.use.logout();

  const searchParams = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const initRanRef = useRef(false);

  /* ---------- Hydrate from cookie + optimistic JWT decode ---------- */
  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;

    installAuthAxiosInterceptor();
    cleanupLegacyArtifacts();

    // Watchdog: never leave the UI stuck in "loading user" state. If hydrate
    // stalls for any reason (e.g. a hung refresh/sync request), force-unblock.
    const watchdog = setTimeout(() => setIsLoading(false), 12_000);

    void hydrateAndSync({
      tokenFromUrl,
      setUser,
      setCustomerId,
      setIsLoading,
      resetStore,
    })
      .catch((err) => {
        // Any unexpected throw must still release the loading state.
        console.error('auth hydrate failed', err);
        setIsLoading(false);
      })
      .finally(() => clearTimeout(watchdog));
  }, [tokenFromUrl, setUser, setCustomerId, setIsLoading, resetStore]);

  /* ---------- Login: OAuth Authorization Code Flow (doc §5.1) ----------
   * 1. Gen state random, save vào HttpOnly cookie via /api/auth/state
   * 2. Save redirect target vào sessionStorage (giữ qua bounce SSO)
   * 3. Redirect ra accounts với client_id + state
   * 4. Accounts mint code (encrypted), redirect về /auth/callback?code=&state=
   * 5. Callback route.ts (server) verify state + exchange code → token + set cookies */
  const login = useCallback(async (redirectTo?: string) => {
    try {
      const target = safeRedirectTarget(
        redirectTo ?? window.location.pathname + window.location.search
      );
      sessionStorage.setItem(AUTH_REDIRECT_KEY, target);

      const state =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);

      // Save state vào HttpOnly cookie qua server route (an toàn hơn sessionStorage)
      const stateRes = await fetch('/api/auth/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state }),
      });
      if (!stateRes.ok) {
        console.error('login: state set failed');
        return;
      }

      const callback = `${window.location.origin}/auth/callback`;
      window.location.href = buildAccountsSignInUrl(callback, state);
    } catch (err) {
      console.error('login failed', err);
    }
  }, []);

  /* ---------- Logout: clear cookies (server + client) + reset stores + nav ----
   * Defense-in-depth: nếu server Set-Cookie bị browser drop (Secure flag mismatch,
   * SameSite quirks...) thì client-side cookie clear vẫn đảm bảo logout thành công.
   * Cũng remove zustand persist storage để tránh re-hydrate user sau hard nav.
   */
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      /* ignore — client-side clear bên dưới vẫn chạy */
    }

    clearTokenCookiesClientSide();
    clearPersistedAuthStorage();
    resetStore();
    clearAuthCache();

    // Hard nav về home — dùng replace để không tạo history entry "/user" mà
    // back-button có thể quay lại sau khi đã logout.
    window.location.replace('/');
  }, [resetStore]);

  const value = useMemo<NewAuthContextValue>(
    () => ({ user, isAuthenticated, isLoading, login, logout }),
    [user, isAuthenticated, isLoading, login, logout]
  );

  return (
    <NewAuthContext.Provider value={value}>{children}</NewAuthContext.Provider>
  );
}

export function useNewAuth() {
  const ctx = useContext(NewAuthContext);
  if (!ctx) throw new Error('useNewAuth must be used within NewAuthProvider');
  return ctx;
}

/* ---------------- Hydrate logic ---------------- */

interface HydrateOpts {
  tokenFromUrl: string | null;
  setUser: (u: AuthUser | null) => void;
  setCustomerId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  resetStore: () => void;
}

async function hydrateAndSync({
  tokenFromUrl,
  setUser,
  setCustomerId,
  setIsLoading,
  resetStore,
}: HydrateOpts) {
  // 1. WebInApp: nếu có ?token=, set cookie trước khi đọc lại
  if (tokenFromUrl) {
    setTokenCookieFromUrl(tokenFromUrl);
    clearTokenFromUrl();
  }

  // 2. Pre-emptive refresh nếu token sắp expire (< 30s) — đảm bảo BE call
  //    đầu tiên (sync) không bị 401 retry storm.
  const token = await ensureValidToken();
  if (!token) {
    // Cookie missing (user xóa tay, hoặc refresh fail) — clear stale persisted
    // state để UI hiện "Chưa đăng nhập" đúng.
    resetStore();
    clearAuthCache();
    setIsLoading(false);
    return;
  }

  // 3. Optimistic — decode JWT, set user state ngay
  const claims = decodeJwtPayload(token);
  if (!claims) {
    setIsLoading(false);
    return;
  }

  // customerId = JWT cid (số điện thoại format 84xxxxxxxxx) — không cần BE sync.
  // JWT là source of truth, available NGAY từ first paint.
  const customerId = claims.cid;

  setUser({
    id: claims.sub,
    phone: claims.cid,
    phoneNumber: claims.cid,
    name: claims.noc,
    email: claims.eoc,
    provider: 'ahamove',
    loginType: 'ahamove',
    systemToken: token,
    cunstomerId: customerId,
  });
  setCustomerId(customerId);
  setIsLoading(false);

  // 4. Background sync — đăng ký session với BE (BE bookkeeping).
  //    FE KHÔNG depend response.customer_id (đã lấy từ JWT). Chỉ check 401 → terminal.
  //    Skip nếu đã sync token này trong 7 ngày (hash match cache).
  const cache = readAuthCache();
  const currentHash = await hashToken(token);
  if (cache && cache.tokenHash === currentHash) {
    return;
  }

  try {
    const syncController = new AbortController();
    const syncTimeout = setTimeout(() => syncController.abort(), 10_000);
    const res = await fetch('/api/auth/sync', {
      method: 'POST',
      signal: syncController.signal,
    }).finally(() => clearTimeout(syncTimeout));
    if (res.status === 401) {
      resetStore();
      setIsLoading(false);
      clearAuthCache();
      await fetch('/api/auth/logout', { method: 'POST' });
      return;
    }
    if (!res.ok) return; // 5xx → keep state, retry next load

    writeAuthCache({
      customerId,
      tokenHash: currentHash,
      syncedAt: Date.now(),
    });
  } catch {
    /* network fail → keep state */
  }
}

/**
 * Xóa cookies + localStorage cũ của old auth flow.
 * Chạy 1 lần khi mount NewAuthProvider — đảm bảo no leftover state.
 */
/** Client-side: detect deployed env qua hostname (NEXT_PUBLIC_APP_ENV không có sẵn). */
function isAhafoodDomain(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.endsWith('.ahafood.ai');
}

function cleanupLegacyArtifacts() {
  try {
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `ws_token=; path=/; ${expire}`;
    if (isAhafoodDomain()) {
      document.cookie = `ws_token=; path=/; domain=.ahafood.ai; ${expire}`;
    }

    if (localStorage.getItem('auth.store')) {
      localStorage.removeItem('auth.store');
    }
  } catch {
    /* ignore */
  }
}

function setTokenCookieFromUrl(token: string) {
  // WebInApp: set cookie từ client để route handlers đọc được.
  // maxAge = 30 ngày — match server-side setAuthCookies, tránh logout sớm
  // khi JWT exp ngắn (refresh chain extend token trước).
  const isHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:';
  const parts = [
    `${COOKIE_TOKEN}=${token}`,
    'path=/',
    'SameSite=Lax',
    `max-age=${30 * 24 * 60 * 60}`,
  ];
  if (isAhafoodDomain()) parts.push('domain=.ahafood.ai');
  if (isHttps) parts.push('Secure');
  document.cookie = parts.join('; ');
}

function clearTokenFromUrl() {
  scheduleParamRemoval('token');
}

/**
 * Client-side cookie clear — chạy mọi variant (host-only + apex domain, có/không
 * Secure). Đảm bảo cookie bị xóa kể cả khi server Set-Cookie response bị browser
 * drop do mismatch SameSite/Secure attributes.
 */
function clearTokenCookiesClientSide() {
  if (typeof document === 'undefined') return;
  const names = ['token', 'refresh_token', 'auth_state'];
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  for (const name of names) {
    document.cookie = `${name}=; path=/; ${expire}`;
    if (isAhafoodDomain()) {
      document.cookie = `${name}=; path=/; domain=.ahafood.ai; ${expire}`;
    }
  }
}

/**
 * Xóa zustand persist storage của auth store. Tránh case sau hard nav, persist
 * middleware hydrate lại user cũ từ localStorage trước khi hydrateAndSync chạy.
 */
function clearPersistedAuthStorage() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('ahafood.new-auth');
  } catch {
    /* ignore */
  }
}
